import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy, DestroyRef, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { fromEvent, Subscription , Subject, takeUntil} from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

@Component({
  selector: 'app-ls-search-input',
  templateUrl: './search-input.component.html',
  styleUrls: ['./search-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class LSSearchInputComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  private readonly destroyRef = inject(DestroyRef);

  @Input() placeholder: string = 'Search';
  @Input() isDatePicker: boolean = false;
  @Input() initialValue: string = '';
  @Output() onSearch: EventEmitter<string> = new EventEmitter();
  @ViewChild('searchInput') searchInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('datePicker') datePickerRef?: any;

  private searchSubscription: Subscription | null = null;

  constructor(private translateService: TranslateService) {
    this.translateService.setDefaultLang('en');
    this.translateService.use(localStorage.getItem('Selected_Language') || 'en');
  }

  ngOnInit(): void {
    // console.log('SearchInputComponent initialized with placeholder:', this.placeholder);
  }

  // Handle dynamic input binding
  @ViewChild('searchInput') set searchInputSetter(input: ElementRef<HTMLInputElement> | undefined) {
    if (input) {
      this.bindSearch(input.nativeElement);
    }
  }

  private bindSearch(inputElement: HTMLInputElement) {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }

    this.searchSubscription = fromEvent(inputElement, 'keyup').pipe(
      map((event: any) => {
        const original = event.target.value || '';
        // Remove only leading spaces (preserve trailing & middle spaces)
        const cleaned = original.replace(/^\s+/, '');

        if (original !== cleaned) {
          event.target.value = cleaned;
        }

        return cleaned;
      }),
      debounceTime(1500),
      map(value => value.trim()), // After pause, trim both ends
      distinctUntilChanged()
    ).pipe(takeUntil(this.destroy$)).subscribe((finalValue: string) => {
      inputElement.value = finalValue; // optional: keep view in sync
      this.onSearch.emit(finalValue);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  // Handle date picker selection
  onDatePicked(date: string) {
  this.onSearch.emit(date);
  }

  setValue(value: string): void {
    if (this.searchInputRef?.nativeElement) {
      this.searchInputRef.nativeElement.value = value ?? '';
    }
  }

  reset(): void {
    // console.log('Resetting search input', this.isDatePicker ? 'Date Picker' : 'Search Input');
    if (this.isDatePicker) {
      // console.log('Date picker reference:', this.datePickerRef);
      if (this.datePickerRef?.reset) {
        // console.log('Resetting date picker');
        this.datePickerRef.reset(); // optional: if your date picker has a reset method
      } else {
        // console.warn('Date picker reset method not found, emitting empty search');
        this.onSearch.emit(''); // fallback: emit empty
      }
    } else {
      if (this.searchInputRef?.nativeElement) {
        this.searchInputRef.nativeElement.value = '';
        this.onSearch.emit('');
      }
    }
  }

}
